import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    fetchAllPracticeFeatureFlags,
    fetchPracticeFeatureFlags,
    togglePracticeFeatureFlag
} from '../services/practiceFeatureFlags';
import { PRACTICE_FEATURE_FLAGS_ALL_KEY, PRACTICE_FEATURE_FLAGS_KEY } from './queryKeys';

export function usePracticeFeatureFlags(practiceId: string | undefined) {
    const query = useQuery({
        queryKey: PRACTICE_FEATURE_FLAGS_KEY(practiceId ?? ''),
        queryFn: () => fetchPracticeFeatureFlags(practiceId as string),
        enabled: Boolean(practiceId),
        staleTime: 60_000
    });

    return {
        features: query.data ?? [],
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error
    };
}

/** Feeds AddBetaFeatureForm's suggestion list - see fetchAllPracticeFeatureFlags. */
export function useAllPracticeFeatureFlags() {
    return useQuery({
        queryKey: PRACTICE_FEATURE_FLAGS_ALL_KEY,
        queryFn: fetchAllPracticeFeatureFlags,
        staleTime: 5 * 60_000
    });
}

interface TogglePracticeFeatureFlagArgs {
    feature: string;
    enabled: boolean;
}

export function useTogglePracticeFeatureFlag(practiceId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ feature, enabled }: TogglePracticeFeatureFlagArgs) =>
            togglePracticeFeatureFlag(practiceId, feature, enabled),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PRACTICE_FEATURE_FLAGS_KEY(practiceId) });
        }
    });
}
